import { css } from '@emotion/react';
import { CancelCode, CancelReason } from '$/GraphQL/schema/schema';
import { isMobile } from '$/utils/isMobile';
import React from 'react';
import tw from 'twin.macro';
import Textarea from '../../../../components/ui/Input/Basics/Textarea';
import RadioButton from '../../../../components/ui/RadioButton';
import Modal from '$/components/ui/Modal';
import Button from '$/components/ui/Button';

interface QuestionnaireItem {
  code: CancelCode;
  reason: string;
  response: string;
}

const questionnaire: QuestionnaireItem[] = [
  {
    code: CancelCode.NotNeeded,
    reason: `Can't make use of this yet, but I'll be back.`,
    response: `Awesome! Any idea when you'll be back?`,
  },
  {
    code: CancelCode.MissingFeature,
    reason: `Missing features I need.`,
    response: `Would you please let us know what features you were hoping to see?`,
  },
  {
    code: CancelCode.TechnicalIssues,
    reason: `Technical issues.`,
    response: `We're sorry to hear that. Please do let us know what issues you've run into. We are here to help!`,
  },
  {
    code: CancelCode.Knowledge,
    reason: `Not sure how to use the service.`,
    response: `Any questions we can help you with? You can contact us anytime by using the chat bubble below.`,
  },
  {
    code: CancelCode.NoMoreWebinars,
    reason: `Won't be doing webinars.`,
    response: `No problem! If you have any feedback to share, we'd love to hear from you.`,
  },
  {
    code: CancelCode.FocusOnLive,
    reason: `Will be focusing on live webinars.`,
    response: `All our customers, including us, have a live webinar solution and use eWebinar to automate everything that is repetitive. Would you like us to share some use cases?`,
  },
  {
    code: CancelCode.TooExpensive,
    reason: `Out of my price range.`,
    response: `Sorry to hear that. Our pricing is based on the fact we don't limit the number of sessions or attendees like most other services. What do you think is a fair monthly subscription price?`,
  },
  {
    code: CancelCode.Other,
    reason: `Other`,
    response: `Would you mind letting us know what we could have done better?`,
  },
];

interface CancelReasonModalProps {
  toggleModal: () => void;
  onOpenCancelSubscriptionModal: () => void;
  setCancelReasonOption: (val?: CancelReason) => void;
  cancelReasonOption?: CancelReason;
}

export const CancelReasonModal: React.FC<CancelReasonModalProps> = ({
  toggleModal,
  onOpenCancelSubscriptionModal,
  setCancelReasonOption,
  cancelReasonOption,
}) => {
  const [waiting, setTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [currentQuestion, setCurrentQuestion] = React.useState<QuestionnaireItem | null>(null);

  const onNext = (): void => {
    onOpenCancelSubscriptionModal();
  };

  return (
    <Modal
      className='md:min-w-35e max-w-35e'
      onClose={toggleModal}
      css={css`
        ${!isMobile ? ' min-height: 479px;' : ''}
      `}
    >
      <Modal.Body className='text-lg pt-0'>
        <Modal.StickyButtonHeader>
          <Modal.Close onClick={toggleModal} />
        </Modal.StickyButtonHeader>
        <Modal.Title className='pt-1_5e'>Oh no, we're sad to see you go.</Modal.Title>
        <p className='text-gray-700 text-md'>
          Before you cancel please let us know the reason you are leaving. Every bit of feedback
          helps!
        </p>
        <ul
          className='ml-0 pt-1/2e mb-0 relative'
          css={css`
            li {
              ${tw`text-gray-700`}
              height: ${isMobile ? 'auto' : '29.5px'};
              transition: height 0.25s;
              .radio {
                top: 2px;
              }
            }
          `}
        >
          {questionnaire.map((question, index) => {
            return (
              <li
                key={index}
                className={`flex overflow-hidden ${
                  cancelReasonOption?.reason
                    ? cancelReasonOption?.reason === question.reason
                      ? ''
                      : 'h-0 opacity-0 pointer-events-none'
                    : ''
                }`}
              >
                <label htmlFor={`reason${index}`} key={`${index}`} className='mr-1 mb-1'>
                  <div className='flex items-sdtart mb-2 cursor-pointer'>
                    <RadioButton.Option
                      id={`reason${index}`}
                      checked={cancelReasonOption?.reason === question.reason}
                      value={question.reason}
                      onChange={(): void => {
                        setCancelReasonOption({
                          code: question.code,
                          reason: question.reason,
                          feedback: '',
                        });

                        setCurrentQuestion(question);
                        setTimer(setTimeout(() => setTimer(null), 250));
                      }}
                    />
                    <RadioButton />
                    <p className='ml-1/2e'>{question.reason}</p>
                  </div>
                </label>
              </li>
            );
          })}
        </ul>
        {cancelReasonOption?.reason && !waiting && (
          <div className='flex flex-col'>
            <div className='mb-1e mt-1e leading-normal'>{currentQuestion?.response ?? ''}</div>

            <Textarea
              placeholder='Enter feedback here'
              onChange={(e): void => {
                setCancelReasonOption({
                  ...cancelReasonOption,
                  feedback: e.target.value,
                });
              }}
              rows={6}
              value={cancelReasonOption.feedback ?? ''}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className='flex items-center justify-between'>
        {cancelReasonOption?.reason ? (
          <Button
            onClick={(): void => {
              setCancelReasonOption(undefined);
            }}
          >
            Back
          </Button>
        ) : (
          <Button onClick={toggleModal}>Continue subscription</Button>
        )}
        <Button.rounded className='px-2e' disabled={!cancelReasonOption?.reason} onClick={onNext}>
          Next
        </Button.rounded>
      </Modal.Footer>
    </Modal>
  );
};
